<template>
  <div class="table-container">
    <table
      class="table"
      :class="{ 'table-vertical': vertical }"
    >
      <thead :class="{'sticky-header': stickyHeader}">
        <tr
          v-for="headerGroup in table.getHeaderGroups()"
          :key="headerGroup.id"
          class="header-row"
        >
          <th
            v-for="header in headerGroup.headers as Array<ITableHeader<T>>"
            :key="header.id"
            :class="getCellClass(header.column.columnDef.meta)"
          >
            <slot
              :name="`header-${header.id}`"
              :header="header"
            >
              <SortingHeader
                v-if="header.column.columnDef.enableSorting && vmSortDirection && vmSortField"
                :header="header"
                v-model:sort-direction="vmSortDirection"
                v-model:sort-field="vmSortField"
                @set-sorting="setSorting"
              />
              <ToggleHeader
                v-if="header.column.columnDef.meta?.toggle"
                :label="header.column.columnDef.header"
                @click="toggleColumn(header.column.id, header.column.columnDef.meta.toggle.targetId)"
              />
              <span v-else>{{ header.column.columnDef.header }}</span>
            </slot>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in table.getRowModel().rows"
          :key="row.id"
          class="body-row group"
          :class="{ 'row-expand-root': row.getIsExpanded(), 'sub-row': row.depth }"
        >
          <td
            v-for="cell in row.getVisibleCells()"
            :key="cell.id"
            :class="getCellClass(cell.column.columnDef.meta)"
          >
            <slot
              :name="`item-${cell.id.split('_').at(-1)}`"
              :row="row"
            >
              <FlexRender :render="cell.getValue()" />
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts" generic="T">
import {
  type ColumnMeta,
  FlexRender,
  getCoreRowModel,
  getExpandedRowModel,
  type TableOptionsWithReactiveData,
  useVueTable, VisibilityState } from '@tanstack/vue-table'
import { useVModel } from '@vueuse/core'

import { generalService } from '~/api/service/general.service'
import SortingHeader from '~/ui/Table/tanstack/header/SortingHeader.vue'
import ToggleHeader from '~/ui/Table/tanstack/header/ToggleHeader.vue'
import { ITableColumnDef, ITableHeader, ITableMeta } from '~/ui/Table/tanstack/table.type'
import { sortDirectionType } from '~/ui/Table/types'

interface IProps {
  columns: Array<ITableColumnDef<T>>
  rows: Array<T>
  expandable?: boolean
  stickyHeader?: boolean
  subRowsName?: string // 하위 리스트의 필드 이름
  sortField?: string
  sortDirection?: sortDirectionType
  vertical?: boolean
}

const props = defineProps<IProps>()
const emits = defineEmits([ 'update:sortField', 'update:sortDirection' ])

const { data: isDelayed } = generalService.useGetDelayCheck()
const desktopStickyTop = computed(() => {
  return isDelayed.value ? '6.5rem' : '4rem'
})
const mobileStickyTop = computed(() => {
  return isDelayed.value ? '5.5rem' : '3rem'
})

const vmSortField = useVModel(props, 'sortField', emits)
const vmSortDirection = useVModel(props, 'sortDirection', emits)

const initialColumnVisibility = props.columns.reduce((acc, cur) => {
  if (cur.id) {
    acc[cur.id] = cur.meta?.toggle?.initialVisible ?? true
  }
  return acc
}, {} as VisibilityState)

const tableOption: TableOptionsWithReactiveData<T> = computed(() => {
  return {
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: props.expandable && getExpandedRowModel(),
    enableExpanding: props.expandable,
    manualSorting: true,
    columns: props.columns,
    data: props.rows,
    getSubRows(originalRow: any) {
      return originalRow[props.subRowsName]
    },
    initialState: {
      columnVisibility: initialColumnVisibility,
    },
  }
})

const table = useVueTable<T>(tableOption)

const getCellClass = (columnMeta?: ColumnMeta<T, unknown> & ITableMeta) => {
  if (!columnMeta) {
    return
  }
  const classList: string[] = []
  if (columnMeta.align) {
    classList.push(`align-${columnMeta.align}`)
  }
  return classList
}

const setSorting = (field?: string) => {
  if (!field) {
    return
  }
  if (field === vmSortField.value) {
    vmSortDirection.value = vmSortDirection.value === sortDirectionType.ASC ? sortDirectionType.DESC :
      sortDirectionType.ASC
    return
  }
  vmSortField.value = field
  vmSortDirection.value = sortDirectionType.DESC
}

const toggleColumn = (from: string, to: string) => {
  table.setColumnVisibility(() => {
    return {
      [from]: false,
      [to]: true,
    }
  })
}
</script>

<style scoped lang="scss">
.align-left {
  &, * {
    @apply text-left justify-start;
  }
}

.align-center {
  &, * {
    @apply text-center justify-center;
  }
}

.align-right {
  &, * {
    @apply text-right justify-end;
  }
}

.table-container {
  @apply w-full relative;
  @include custom-scrollbar;

  .table {
    @apply w-full border-separate border-spacing-0 text-left text-lg text-primary/40 relative;

    thead {
      &.sticky-header {
        @apply sticky z-10 mobile:top-12;
        top: v-bind(desktopStickyTop);
        
        @include sm () {
          top: v-bind(mobileStickyTop);
        }

        @include background-blur(16px);
      }
    }

    tr {
      @apply relative h-14;

      &.header-row {

      }

      &.body-row {
        &::after {
          @apply rounded-lg bg-gradient-to-r from-primary/5 to-point-color/20 shadow-[0px_16px_32px] shadow-primary/5  transition-all duration-300 ease-[ease] opacity-0 absolute top-0 left-0 w-full h-full content-[''] z-[-1];
        }


        &:hover::after {
          @apply opacity-100;
        }
      }

      //&.row-expand-root {
      //  @apply cursor-pointer;
      //}

    }

    th, td {
      @apply p-4 whitespace-nowrap;
    }

    th {
      @apply text-base font-normal text-primary border-y border-primary/20;
    }

    td {

    }

    &.table-vertical {
      @apply flex flex-row;

      thead, tbody {
        @apply flex;
      }

      tr {
        @apply flex flex-col;
      }

      th, td {
        @apply block;
      }
    }
  }
}
</style>